type DataPoint = {
  timestamp: number;
  value: number;
};

type InputData = {
  usergroup_id: number;
  stat_type: string;
  stat_subtype: string;
  start_time: number | string;
  end_time: number | string;
  data: {
    [key: string]: DataPoint[];
  };
};

const DAY = 86400;

// Candidate bucket sizes, smallest first. A bucket is either a fixed number
// of seconds or a number of calendar months. Nothing below a day: the stats
// tables are daily, and the chart labels points by date only.
const STEPS: ({ secs: number } | { months: number })[] = [
  { secs: DAY },
  { secs: 2 * DAY },
  { secs: 3 * DAY },
  { secs: 7 * DAY },
  { secs: 14 * DAY },
  { months: 1 },
  { months: 3 },
  { months: 6 },
  { months: 12 },
];

const monthIndex = (ts: number) => {
  const d = new Date(ts * 1000);
  return d.getUTCFullYear() * 12 + d.getUTCMonth();
};

const monthStart = (index: number) => Date.UTC(Math.floor(index / 12), index % 12, 1) / 1000;

/**
 * Groups a time series into equal TIME buckets, at most `maxItems` of them.
 *
 * Each point is labelled with the start of its bucket and holds the sum of
 * every record whose timestamp falls inside it (storage, which is cumulative,
 * keeps the latest value instead).
 *
 * This used to merge neighbouring records in pairs, ignoring their dates.
 * That only works when every record covers the same length of time. The CDN
 * series does not: CDN77 returns one record per month for long ranges, mixed
 * in with daily Cloudflare rows, so a "Past 1 Year" chart showed one point
 * covering nine months next to 18-day points that flipped between TB and KB
 * depending on whether they happened to contain a 1st-of-month CDN77 record.
 * Bucketing by date puts every record in the period it belongs to,
 * whatever granularity each source reports at.
 */
function rollupData(input: InputData, maxItems: number = 13, statType?: string): InputData {
  const result: InputData = JSON.parse(JSON.stringify(input));

  for (const key in result.data) {
    const records = [...result.data[key]].sort((a, b) => a.timestamp - b.timestamp);
    if (records.length === 0) continue;

    const first = records[0].timestamp;
    const last = records[records.length - 1].timestamp;
    // Fixed-size buckets start on the first record's UTC day.
    const origin = Math.floor(first / DAY) * DAY;

    // Smallest step that fits the range into maxItems buckets.
    const step =
      STEPS.find((s) =>
        'secs' in s
          ? Math.floor((last - origin) / s.secs) + 1 <= maxItems
          : Math.floor((monthIndex(last) - monthIndex(first)) / s.months) + 1 <= maxItems,
      ) ?? STEPS[STEPS.length - 1];

    const bucketStart = (ts: number) => {
      if ('secs' in step) {
        return origin + Math.floor((ts - origin) / step.secs) * step.secs;
      }
      const base = monthIndex(first);
      return monthStart(base + Math.floor((monthIndex(ts) - base) / step.months) * step.months);
    };

    const buckets = new Map<number, number>();
    // Additive metrics: a bucket with no records is a real zero, and keeping
    // it keeps the x-axis evenly spaced. Storage has no meaningful zero, so
    // its empty buckets are left out.
    if (statType !== 'storage') {
      for (let b = bucketStart(first), i = 1; b <= last; i++) {
        buckets.set(b, 0);
        b =
          'secs' in step
            ? bucketStart(first) + i * step.secs
            : monthStart(monthIndex(first) + i * step.months);
      }
    }
    for (const r of records) {
      const b = bucketStart(r.timestamp);
      // Records are sorted, so for storage the last write is the latest value.
      buckets.set(b, statType === 'storage' ? r.value : (buckets.get(b) ?? 0) + r.value);
    }

    result.data[key] = Array.from(buckets, ([timestamp, value]) => ({ timestamp, value }));
  }

  return result;
}

export default rollupData;
